import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  createSceneTransform,
  createUnconstrainedSceneTransform,
} from "./geometry";
import { constrainSticker } from "./stickerEditing";
import {
  AvailableSticker,
  HeaderViewportMode,
  PetHeaderComposition,
  PetHeaderSticker,
  SceneSize,
} from "./types";
import cls from "./stickerEditor.module.scss";

interface Props {
  onDragState?: (id: string | null) => void;
  composition: PetHeaderComposition;
  mode: HeaderViewportMode;
  viewport: SceneSize;
  overflow: boolean;
  catalog: AvailableSticker[];
  selectedId: string | null;
  disabled: boolean;
  onSelect: (id: string | null) => void;
  onChange: (sticker: PetHeaderSticker) => void;
  onDelete: (id: string) => void;
}

export function StickerInteractionLayer({
  composition,
  mode,
  viewport,
  overflow,
  catalog,
  selectedId,
  disabled,
  onSelect,
  onChange,
  onDelete,
  onDragState,
}: Props) {
  const { t } = useTranslation();
  const drag = useRef<{
    id: number;
    x: number;
    y: number;
    resize: boolean;
    sticker: PetHeaderSticker;
  } | null>(null);
  const image = {
    width: composition.header.canvasWidth,
    height: composition.header.canvasHeight,
  };
  const crop =
    mode === "mobile" ? composition.mobileCrop : composition.desktopCrop;
  const transform = (overflow
    ? createUnconstrainedSceneTransform
    : createSceneTransform)(crop, image, viewport);
  const imageWidth = image.width * transform.scale;
  const imageHeight = image.height * transform.scale;
  const slots = [...composition.header.depthSlots].sort(
    (a, b) => a.order - b.order
  );
  const ordered = [...composition.stickers, ...composition.fireflies].sort(
    (a, b) =>
      slots.findIndex((s) => s.key === a.depthSlot) -
        slots.findIndex((s) => s.key === b.depthSlot) ||
      a.stackOrder - b.stackOrder
  );

  const update = (next: PetHeaderSticker) => {
    const preset = catalog.find((item) => item.asset.key === next.assetId);
    if (preset) onChange(constrainSticker(next, preset.defaultWidth, image));
  };
  const stop = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    onDragState?.(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <>
      {composition.stickers.map((sticker) => (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={sticker.asset.name}
          aria-pressed={selectedId === sticker.id}
          aria-disabled={disabled}
          data-sticker-hit={sticker.assetId}
          className={cls.stickerHit}
          key={sticker.id}
          style={{
            // Keep captured DOM nodes in place when the visual stacking changes.
            zIndex:
              selectedId === sticker.id
                ? ordered.length
                : ordered.findIndex((s) => s.id === sticker.id),
            left: transform.offsetX + sticker.x * imageWidth,
            top: transform.offsetY + sticker.y * imageHeight,
            width: sticker.width * imageWidth,
            height:
              (sticker.width * imageWidth * sticker.asset.imageHeight) /
              sticker.asset.imageWidth,
          }}
          onPointerDown={(event) => {
            event.stopPropagation();
            if (
              disabled ||
              drag.current ||
              (event.pointerType === "mouse" && event.button !== 0)
            )
              return;
            event.preventDefault();
            onSelect(sticker.id);
            onDragState?.(sticker.id);
            event.currentTarget.focus({ preventScroll: true });
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = {
              id: event.pointerId,
              x: event.clientX,
              y: event.clientY,
              resize: Boolean(
                (event.target as HTMLElement).closest("[data-sticker-resize]")
              ),
              sticker,
            };
          }}
          onPointerMove={(event) => {
            event.stopPropagation();
            const start = drag.current;
            if (disabled || !start || start.id !== event.pointerId) return;
            event.preventDefault();
            const dx = (event.clientX - start.x) / imageWidth;
            const dy = (event.clientY - start.y) / imageHeight;
            const aspect =
              start.sticker.asset.imageHeight / start.sticker.asset.imageWidth;
            update(
              start.resize
                ? {
                    ...start.sticker,
                    width:
                      start.sticker.width +
                      (event.clientX -
                        start.x +
                        (event.clientY - start.y) * aspect) /
                        (imageWidth * (1 + aspect * aspect)),
                  }
                : {
                    ...start.sticker,
                    x: start.sticker.x + dx,
                    y: start.sticker.y + dy,
                  }
            );
          }}
          onPointerUp={stop}
          onPointerCancel={stop}
          onLostPointerCapture={stop}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (disabled) return;
            const steps: Record<string, [number, number]> = {
              ArrowLeft: [-1, 0],
              ArrowRight: [1, 0],
              ArrowUp: [0, -1],
              ArrowDown: [0, 1],
            };
            const step = steps[event.key];
            if (step) {
              event.preventDefault();
              update({
                ...sticker,
                x:
                  sticker.x +
                  (step[0] * (event.shiftKey ? 20 : 5)) / imageWidth,
                y:
                  sticker.y +
                  (step[1] * (event.shiftKey ? 20 : 5)) / imageHeight,
              });
            } else if (["Enter", " "].includes(event.key)) {
              event.preventDefault();
              onSelect(sticker.id);
            } else if (["Delete", "Backspace"].includes(event.key)) {
              event.preventDefault();
              onDelete(sticker.id);
            } else if (event.key === "Escape") onSelect(null);
          }}
        >
          {selectedId === sticker.id && (
            <button
              type="button"
              className={cls.resizeHandle}
              data-sticker-resize
              disabled={disabled}
              tabIndex={-1}
              aria-label={t("petHeader.stickerSize")}
              title={t("petHeader.stickerSize")}
            />
          )}
        </div>
      ))}
    </>
  );
}
